'use client'

import { MediaImageStandard, Typography } from 'components/Contentful'
import { useEffect, useRef, useState } from 'react'

const createImageContent = (src) => ({
  fields: {
    file: {
      url: src,
      fileName: 'bottom-display-image',
    },
  },
})

/**
 * BottomDisplay
 *
 * Renders a continuously auto-scrolling marquee strip.
 *
 * Each item is one of:
 * - Image card:  { image: string, alt?: string }
 * - Text card:   { label: string, value: string }
 */
function BottomDisplay({ items = [], spin = false }) {
  const containerRef = useRef(null)
  const measureInnerRef = useRef(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [animOffset, setAnimOffset] = useState(null)

  useEffect(() => {
    const onInteract = () => setHasInteracted(true)
    window.addEventListener('mousemove', onInteract, { once: true })
    window.addEventListener('touchstart', onInteract, { once: true })
    return () => {
      window.removeEventListener('mousemove', onInteract)
      window.removeEventListener('touchstart', onInteract)
    }
  }, [])

  useEffect(() => {
    const checkLayout = () => {
      if (!containerRef.current || !measureInnerRef.current) return
      const contentWidth = measureInnerRef.current.scrollWidth
      const gap = parseFloat(getComputedStyle(measureInnerRef.current).columnGap) || 0
      setHasOverflow(contentWidth > containerRef.current.clientWidth)
      setAnimOffset(contentWidth + gap)
    }

    checkLayout()
    window.addEventListener('resize', checkLayout)

    return () => window.removeEventListener('resize', checkLayout)
  }, [items])

  if (!items.length) return null

  const shouldAnimate = spin && hasInteracted && animOffset !== null

  const renderCard = (item, key) => {
    const hasText = item.overline || item.title || (item.tags && item.tags.length > 0)

    return (
      <div
        key={key}
        className={`flex ${
          item.image && hasText ? 'flex-row items-center gap-3' : 'flex-col items-center gap-1'
        } shrink-0 bg-white rounded-[12px] px-4 py-0`}
      >
        {item.image ? (
          <>
            <div className="h-[78px] breakpointMd:h-[92px] flex items-center justify-center shrink-0">
              <MediaImageStandard
                content={createImageContent(item.image)}
                alt={item.alt || 'carousel item image'}
                className="h-full w-auto object-contain"
                priority
              />
            </div>
            {hasText && (
              <div className="flex flex-col justify-between h-[78px] breakpointMd:h-[92px] max-w-[185px] w-[185px]  py-2">
                {item.overline && (
                  <span className="font-ultramarine font-bold text-[14px] leading-[120%] tracking-[3.5px] uppercase text-[#00A86B]">
                    {item.overline}
                  </span>
                )}
                {item.title && (
                  <span className="font-ultramarine font-bold text-[16px] leading-[20px] text-[#1D252F]">
                    {item.title}
                  </span>
                )}
                {item.tags && item.tags.length > 0 && (
                  <span className="font-ultramarine text-[14px] leading-[16px] text-[#5C708A]">
                    <span className="font-normal">{item.tags[0]}</span>
                    {item.tags[1] && (
                      <>
                        <span className="font-normal">{' • '}</span>
                        <span className="font-bold">{item.tags[1]}</span>
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <Typography
              component="span"
              responsive={true}
              className="font-ultramarine font-medium text-center text-[#1D252F] text-[36px] leading-[120%] breakpointMd:text-[48px] breakpointMd:tracking-[-0.48px] whitespace-nowrap"
              content={item.label}
            />
            <Typography
              component="span"
              responsive={true}
              className="font-ultramarine font-normal text-center text-[#5C708A] text-[16px] leading-[140%] whitespace-nowrap"
              content={item.value}
            />
          </>
        )}
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden"
      style={{ WebkitTransform: 'translateZ(0)', transform: 'translateZ(0)' }}
    >
      <style>{`
        @keyframes hero-marquee {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(var(--marquee-offset), 0, 0); }
        }
      `}</style>

      <div className="absolute invisible pointer-events-none h-0 overflow-hidden">
        <div ref={measureInnerRef} className="flex items-center gap-4 breakpointMd:gap-16 w-max">
          {items.map((item, index) => renderCard(item, `measure-${index}`))}
        </div>
      </div>

      {shouldAnimate ? (
        <div
          className="flex items-center gap-4 breakpointMd:gap-16 w-max"
          style={{
            '--marquee-offset': `-${animOffset}px`,
            animation: 'hero-marquee 24s linear infinite',
            animationPlayState: isHovered ? 'paused' : 'running',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex items-center gap-4 breakpointMd:gap-16 shrink-0">
            {items.map((item, index) => renderCard(item, `animated-a-${index}`))}
          </div>
          <div className="flex items-center gap-4 breakpointMd:gap-16 shrink-0" aria-hidden="true">
            {items.map((item, index) => renderCard(item, `animated-b-${index}`))}
          </div>
        </div>
      ) : (
        <div
          className={`${
            hasOverflow ? 'w-max' : 'w-full justify-center'
          } flex items-center gap-4 breakpointMd:gap-16`}
        >
          {items.map((item, index) => renderCard(item, `static-${index}`))}
        </div>
      )}
    </div>
  )
}

export default BottomDisplay
